import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import classnames from 'classnames';
import isString from 'lodash/isString';
import throttle from 'lodash/throttle';
import FAQSection from '../FAQSection';
import TopNavBar from '../TopNavBar';
import { Button, Image } from 'shared/lib/SureDesignSystem';
import Footer from '../Footer';

import { ReactComponent as SureWordmark } from 'shared/lib/assets/img/logo/logo-sure-wordmark.svg';
import { ReactComponent as CoverageDetailShape } from './assets/coverage-detail-shape.svg';

import styles from './LandingTemplate.module.css';

const propTypes = {
  config: {
    hero: PropTypes.shape({
      heading: PropTypes.string.isRequired,
      subheading: PropTypes.string.isRequired,
      cta: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
        .isRequired,
      ctaLink: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
        .isRequired,
      img: PropTypes.node.isRequired,
    }),
    underwriter: PropTypes.shape({
      img: PropTypes.node.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    coverageDetails: PropTypes.arrayOf(
      PropTypes.shape({
        heading: PropTypes.string.isRequired,
        subheading: PropTypes.string.isRequired,
      })
    ).isRequired,
    scenarioDetails: PropTypes.shape({
      heading: PropTypes.string.isRequired,
      scenarios: PropTypes.arrayOf(
        PropTypes.shape({
          heading: PropTypes.string.isRequired,
          subheading: PropTypes.string.isRequired,
        })
      ),
    }).isRequired,
    comparison: PropTypes.arrayOf(PropTypes.string).isRequired,
    faq: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
      })
    ).isRequired,
  },
};

class LandingTemplate extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      windowWidth: Math.max(
        document.documentElement.clientWidth,
        window.innerWidth || 0
      ),
      activeFaqItemId: null,
    };
    this.throttledUpdatedWidth = throttle(this.updateWidth, 1000);
  }

  componentDidMount() {
    window.addEventListener('resize', this.throttledUpdatedWidth);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.throttledUpdatedWidth);
  }

  updateWidth = () => {
    const windowWidth = Math.max(
      document.documentElement.clientWidth,
      window.innerWidth || 0
    );
    this.setState({ windowWidth });
  };

  get heroSection() {
    const { hero } = this.props.config;
    const { CustomCTA } = hero;
    return (
      <header className={styles.heroSection}>
        <div className={styles.heroSection_wrapper}>
          <h1 className={styles.heroSection_heading}>{hero.heading}</h1>
          <h3 className={styles.heroSection_subheading}>{hero.subheading}</h3>
          <div className={styles.heroSection_imgWrapper}>
            <Image className={styles.heroSection_img} src={hero.img} alt="" />
          </div>
          {CustomCTA || (
            <Button
              className={styles.heroSection_ctaBtn}
              data-testid="landing_cta"
              {...(isString(hero.ctaLink)
                ? {
                    to: {
                      pathname: hero.ctaLink,
                      query: this.props.location.query,
                    },
                  }
                : {
                    onClick: hero.ctaLink,
                  })}
            >
              {hero.cta}
            </Button>
          )}
        </div>
      </header>
    );
  }

  get underwriterSection() {
    const { underwriter } = this.props.config;
    return (
      <section className={styles.underwriterSection}>
        <div className={styles.underwriterSection_wrapper}>
          <p className={styles.underwriterSection_copy}>Coverage backed by</p>
          <Image
            className={styles.underwriterSection_logo}
            src={underwriter.img}
            alt={underwriter.name}
          />
        </div>
      </section>
    );
  }

  get coverageDetailsSection() {
    const { coverageDetails } = this.props.config;
    return (
      <section className={styles.coverageDetailsSection}>
        <h1 className={styles.coverageDetailsSection_heading}>
          Coverage details
        </h1>
        <div className={styles.coverageDetailsSection_pane}>
          {coverageDetails.map(coverageDetail => (
            <div
              className={styles.coverageDetailsSection_item}
              key={coverageDetail.heading}
            >
              <h2 className={styles.coverageDetailsSection_itemHeading}>
                {coverageDetail.heading}
              </h2>
              <p className={styles.coverageDetailsSection_subHeading}>
                {coverageDetail.subheading}
              </p>
            </div>
          ))}
          <CoverageDetailShape
            className={styles.coveragerDetailSection_shape}
          />
        </div>
      </section>
    );
  }

  get scenarioDetailsSection() {
    const { scenarioDetails } = this.props.config;
    return (
      <section className={styles.scenarios}>
        <div className={styles.scenarios_wrapper}>
          <h1 className={styles.scenarios_heading}>
            {scenarioDetails.heading}
          </h1>
          {this.state.windowWidth >= 768 ? (
            <div className={styles.scenarios_cardGrid}>
              {scenarioDetails.scenarios.map(scenarioDetail => (
                <div
                  className={styles.scenarios_cardFlip}
                  key={scenarioDetail.heading}
                >
                  <div className={styles.scenarios_cardSpacer}>
                    <div
                      className={classnames(
                        styles.scenarios_cardWrapper,
                        styles.scenarios_cardFront
                      )}
                    >
                      <div className={styles.scenarios_cardContent}>
                        <div className={styles.scenarios_cardHeading}>
                          {scenarioDetail.heading}
                        </div>
                      </div>
                    </div>
                    <div
                      className={classnames(
                        styles.scenarios_cardWrapper,
                        styles.scenarios_cardBack
                      )}
                    >
                      <div className={styles.scenarios_cardContent}>
                        <div className={styles.scenarios_cardSubheading}>
                          {scenarioDetail.subheading}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Slider
              adaptiveHeight={false}
              slidesToShow={1}
              infinite={false}
              arrows={false}
              dots={false}
              centerPadding="0px"
              centerMode={true}
            >
              {scenarioDetails.scenarios.map(scenarioDetail => (
                <div
                  className={styles.scenarios_cardWrapper}
                  key={scenarioDetail.heading}
                >
                  <div className={styles.scenarios_cardContainer}>
                    <div className={styles.scenarios_cardContent}>
                      <div
                        className={styles.scenarios_cardSubheading}
                        data-key={scenarioDetail.heading}
                      >
                        {scenarioDetail.subheading}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          )}
        </div>
      </section>
    );
  }

  get comparisonSection() {
    const { comparison } = this.props.config;
    return (
      <section className={styles.comparisonSection}>
        <h1 className={styles.comparisonSection_heading}>How we stack up</h1>
        <div className={styles.comparisonSection_tableWrapper}>
          <div className={styles.comparisonSection_table}>
            <div className={styles.comparisonSection_tableHeaderRow}>
              <div className={styles.comparisonSection_tableHeaderCell}>
                Feature
              </div>
              <div className={styles.comparisonSection_tableHeaderCell}>
                <SureWordmark
                  className={styles.comparisonSection_sureWordmark}
                  alt="Sure"
                />
              </div>
              <div className={styles.comparisonSection_tableHeaderCell}>
                The rest
              </div>
            </div>
            {comparison.map(feature => (
              <div className={styles.comparisonSection_tableRow}>
                <div className={styles.comparisonSection_tableCell}>
                  {feature}
                </div>
                <div className={styles.comparisonSection_tableCell}>
                  <i className="fas fa-check" />
                </div>
                <div className={styles.comparisonSection_tableCell}>
                  <i className="fas fa-times" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  render() {
    const { CustomFirstSection } = this.props.config;
    return (
      <div className={styles.wrapper}>
        <TopNavBar />
        <main className={styles.content}>
          {this.heroSection}
          {this.underwriterSection}
          {CustomFirstSection}
          {this.coverageDetailsSection}
          {this.scenarioDetailsSection}
          {this.comparisonSection}
          <FAQSection />
        </main>
        <Footer />
      </div>
    );
  }
}

LandingTemplate.propTypes = propTypes;

export default LandingTemplate;
